import { useCallback, useEffect, useState } from 'react';

import {
  listInvestorApplications, approveInvestorApplication, rejectInvestorApplication,
  listInvestmentInquiries, markInquiryContacted,
  type InvestorApplication, type InvestorApplicationCounts, type InvestmentInquiry,
} from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatMoney, formatDateTime, formatDateShort } from '../../lib/format';
import './AdminPages.css';

/**
 * Investor approvals.
 *
 * This screen exists because a real submission went missing. Two things reach Voleto from
 * someone who wants to invest, and neither had anywhere to be seen:
 *
 *   APPLICATIONS come from a registered investor who submitted accreditation. They were
 *   reachable only as ordinary rows in the Users list, which offers an approve action
 *   just where an investor account already exists, so an application looked like any
 *   other account among hundreds.
 *
 *   INQUIRIES come from the public offering page. No login, no account, no accreditation.
 *   They have been written to the database since task 41a and read by nothing at all, so
 *   every one of them was invisible from the moment it was submitted.
 *
 * They are shown as two lists rather than one because they are not the same thing and
 * support different actions. An inquiry cannot be approved: there is no account to
 * approve, and the only honest action is recording that a human followed up.
 */

const METHOD_LABEL: Record<string, string> = {
  income: 'Income (over $200k, or $300k jointly)',
  net_worth: 'Net worth (over $1M, excluding primary residence)',
  professional: 'Professional certification',
  entity: 'Entity (over $5M in assets)',
};

const STATE_LABEL: Record<string, { label: string; tone: string }> = {
  pending_review: { label: 'Awaiting review', tone: 'badge--warn' },
  approved: { label: 'Approved', tone: 'badge--ok' },
  rejected: { label: 'Declined', tone: 'badge--danger' },
  not_submitted: { label: 'No application', tone: 'badge--muted' },
};

function ApplicationRow({ app, onChanged }: {
  app: InvestorApplication; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const meta = STATE_LABEL[app.state] ?? STATE_LABEL.not_submitted;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try { await fn(); onChanged(); } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
      setBusy(false);
    }
  }

  return (
    <article className="net-row">
      <div className="net-row__main">
        <span className="net-row__name">{app.name ?? 'Name not provided'}</span>
        <span className="net-row__email mono">{app.email}</span>
        <div className="net-row__badges">
          <span className={`badge ${meta.tone}`}>{meta.label}</span>
          {app.isTestPersona && <span className="badge badge--muted">Test persona</span>}
        </div>

        <span className="opp-reg__hint">
          Registered {formatDateShort(app.registeredAt)}
          {app.submittedAt ? ` · applied ${formatDateTime(app.submittedAt)}` : ' · no application submitted'}
        </span>

        {app.accreditationMethod && (
          <span className="opp-reg__hint">
            Basis: {METHOD_LABEL[app.accreditationMethod] ?? app.accreditationMethod}
          </span>
        )}

        {app.accreditationDocUrl && (
          <span className="opp-reg__hint">
            <a className="adm-link" href={app.accreditationDocUrl}
              target="_blank" rel="noreferrer noopener">
              Supporting document
            </a>
          </span>
        )}

        {app.state === 'approved' && (
          <span className="opp-reg__hint">
            Approved {formatDateTime(app.approvedAt)}
            {app.approvedBy ? ` by ${app.approvedBy}` : ''}
          </span>
        )}
        {app.state === 'rejected' && (
          <span className="opp-reg__hint">
            Declined {formatDateTime(app.rejectedAt)}
            {app.reviewedBy ? ` by ${app.reviewedBy}` : ''}: {app.rejectionReason}
          </span>
        )}

        {error && <p className="adm-error" role="alert">{error}</p>}
      </div>

      <div className="net-row__actions">
        {app.canApprove && (
          <button type="button" className="button button--primary adm-btn-sm" disabled={busy}
            onClick={() => void run(() => approveInvestorApplication(app.userId))}>
            Approve
          </button>
        )}
        {app.canReject && (
          <button type="button" className="button button--ghost adm-btn-sm sup-danger"
            disabled={busy} onClick={() => setDeclining((v) => !v)}>
            {declining ? 'Cancel' : 'Decline'}
          </button>
        )}
      </div>

      {declining && (
        <div className="net-row__panel">
          {/* A reason is required. A declined application with no recorded reason cannot
              be explained later, to the applicant or to anyone auditing the decision. */}
          <label className="field wiz-field--wide">
            <span className="field__label">Reason for declining *</span>
            <input className="field__input" type="text" value={reason}
              placeholder="What the evidence did not establish"
              onChange={(e) => setReason(e.target.value)} />
          </label>
          <button type="button" className="button button--primary"
            disabled={busy || reason.trim().length < 4}
            onClick={() => void run(() => rejectInvestorApplication(app.userId, reason.trim()))}>
            Confirm decline
          </button>
        </div>
      )}
    </article>
  );
}

function InquiryRow({ inquiry, onChanged }: {
  inquiry: InvestmentInquiry; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <article className="net-row">
      <div className="net-row__main">
        <span className="net-row__name">{inquiry.name ?? 'Name not provided'}</span>
        <span className="net-row__email mono">{inquiry.email}</span>
        <div className="net-row__badges">
          {inquiry.contactedAt
            ? <span className="badge badge--ok">Followed up</span>
            : <span className="badge badge--warn">Awaiting contact</span>}
          {inquiry.selfAttestedAccredited && (
            <span className="badge badge--muted">Self attested accredited</span>
          )}
        </div>
        <span className="opp-reg__hint">
          {formatDateTime(inquiry.createdAt)}
          {inquiry.desiredAmountCents
            ? ` · interested in ${formatMoney(inquiry.desiredAmountCents)}`
            : ''}
          {inquiry.opportunityTitle ? ` · ${inquiry.opportunityTitle}` : ''}
        </span>
        <span className="opp-reg__hint">
          {inquiry.userEmail
            ? `Signed in as ${inquiry.userEmail}`
            : 'Submitted without an account. There is nothing to approve here; contact them to begin an application.'}
        </span>
        {inquiry.contactedAt && (
          <span className="opp-reg__hint">
            Contacted {formatDateTime(inquiry.contactedAt)}
            {inquiry.contactedBy ? ` by ${inquiry.contactedBy}` : ''}
          </span>
        )}
        {error && <p className="adm-error" role="alert">{error}</p>}
      </div>

      <div className="net-row__actions">
        <a className="adm-link adm-btn-sm" href={`mailto:${inquiry.email}`}>Email</a>
        {!inquiry.contactedAt && (
          <button type="button" className="button button--ghost adm-btn-sm" disabled={busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              markInquiryContacted(inquiry.id).then(onChanged).catch((err: unknown) => {
                setError(err instanceof ApiError ? err.message : 'Could not record that.');
                setBusy(false);
              });
            }}>
            Mark contacted
          </button>
        )}
      </div>
    </article>
  );
}

const TABS: Array<{ key: string; label: string; countKey: keyof InvestorApplicationCounts | null }> = [
  { key: 'pending_review', label: 'Awaiting review', countKey: 'pendingReview' },
  { key: 'approved', label: 'Approved', countKey: 'approved' },
  { key: 'rejected', label: 'Declined', countKey: 'rejected' },
  { key: 'all', label: 'All accounts', countKey: null },
];

export function AdminInvestorApprovals() {
  const [tab, setTab] = useState('pending_review');
  const [apps, setApps] = useState<InvestorApplication[] | null>(null);
  const [counts, setCounts] = useState<InvestorApplicationCounts | null>(null);
  const [inquiries, setInquiries] = useState<InvestmentInquiry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    listInvestorApplications(tab)
      .then((r) => { setApps(r.applications); setCounts(r.counts); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load applications.'));
    listInvestmentInquiries().then(setInquiries).catch(() => setInquiries([]));
  }, [tab]);

  useEffect(load, [load]);

  const uncontacted = (inquiries ?? []).filter((i) => !i.contactedAt).length;

  return (
    <div className="adm-page" data-surface="invest">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Investor approvals</h1>
          <p className="adm-lede">
            Accreditation applications from registered investors, and expressions of
            interest from the public offering page. They are separate pipelines: an
            inquiry has no account behind it and cannot be approved, only followed up.
          </p>
        </div>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <section className="adm-section">
        <h2 className="adm-section__title">Accreditation applications</h2>

        <div className="adm-filters">
          {TABS.map((t) => (
            <button key={t.key} type="button"
              className={`adm-tab${tab === t.key ? ' is-active' : ''}`}
              onClick={() => setTab(t.key)}>
              {t.label}
              {t.countKey && counts ? ` (${counts[t.countKey]})` : ''}
            </button>
          ))}
        </div>

        {apps == null && <p className="adm-status">Loading…</p>}
        {apps != null && apps.length === 0 && (
          <p className="adm-status adm-muted">
            {tab === 'pending_review'
              ? 'No applications awaiting review.'
              : 'Nothing in this state.'}
          </p>
        )}

        <div className="adm-list">
          {(apps ?? []).map((a) => (
            <ApplicationRow key={a.userId} app={a} onChanged={load} />
          ))}
        </div>
      </section>

      <section className="adm-section">
        <h2 className="adm-section__title">
          Inquiries from the offering page
          {uncontacted > 0 ? ` (${uncontacted} awaiting contact)` : ''}
        </h2>
        <p className="adm-lede">
          Submitted without signing in, so there is no account and no accreditation on
          file. Contact them to start a real application.
        </p>

        {inquiries != null && inquiries.length === 0 && (
          <p className="adm-status adm-muted">No inquiries yet.</p>
        )}

        <div className="adm-list">
          {(inquiries ?? []).map((i) => (
            <InquiryRow key={i.id} inquiry={i} onChanged={load} />
          ))}
        </div>
      </section>
    </div>
  );
}
